"use client";

import { useEffect, useMemo, useState } from "react";
import type { CmsCalculatorItem } from "@/lib/cms-data";
import type { CalculatorId, CalcLang } from "@/lib/calculator-shared";
import {
  getCalculatorList,
  getCalculatorMeta,
  UI_STRINGS,
  CALCULATOR_LIST,
  SHARED_STRINGS,
} from "@/lib/calculator-shared";

// ============================================================
// 紧固件在线计算器主组件
// 包含 10 个专业计算器：
//  1. 公英制单位换算器
//  2. 螺栓拧紧扭矩计算器（公制）
//  3. 螺纹底孔直径计算器
//  4. 硬度换算计算器（HRC/HB/HV）
//  5. 螺栓拉伸应力校核
//  6. 英制 UNC/UNF 扭矩计算器
//  7. 螺栓剪切强度校核
//  8. 螺纹最小旋合长度计算
//  9. 螺栓长度选型计算器
// 10. 连接面滑移安全系数校核
// 类型/列表/校验 定义在 @/lib/calculator-shared（供 Server 与 Client 共享）
// ============================================================

export type { CalculatorId, CalcLang };
export { CALCULATOR_LIST };

function CalculatorPanel({ id, lang = "zh" as CalcLang }: { id: CalculatorId; lang?: CalcLang }) {
  switch (id) {
    case "unit-converter":
      return <UnitConverter lang={lang} />;
    case "torque-metric":
      return <TorqueMetric lang={lang} />;
    case "tap-drill":
      return <TapDrill lang={lang} />;
    case "hardness":
      return <HardnessConverter lang={lang} />;
    case "tension-check":
      return <TensionCheck lang={lang} />;
    case "torque-unified":
      return <TorqueUnified lang={lang} />;
    case "shear-check":
      return <ShearCheck lang={lang} />;
    case "min-engagement":
      return <MinEngagement lang={lang} />;
    case "bolt-length":
      return <BoltLengthSelector lang={lang} />;
    case "slip-factor":
      return <SlipFactorCheck lang={lang} />;
  }
}

// 主页模式：显示 10 张卡片列表（点击卡片跳转子页面显示对应计算器）
export function FastenerCalculator({ lang = "zh" as CalcLang }: { lang?: CalcLang }) {
  const [cmsItems, setCmsItems] = useState<CmsCalculatorItem[] | null>(null);
  const fallbackList = getCalculatorList(lang);
  const ui = UI_STRINGS[lang];
  const listPath = lang === "en" ? "/en/calculator" : "/calculator";
  const list = cmsItems
    ? cmsItems
      .filter((item) => item.visible !== false)
      .sort((a, b) => a.order - b.order)
      .map((item) => ({
        id: item.id,
        title: lang === "en" ? item.enTitle : item.zhTitle,
        desc: lang === "en" ? item.enDesc : item.zhDesc,
        href: item.kind === "custom"
          ? item.href
          : `${listPath}/${item.calculatorId}`,
      }))
    : fallbackList.map((item) => ({ ...item, href: `${listPath}/${item.id}` }));

  useEffect(() => {
    let cancelled = false;
    fetch("/api/calculators", { cache: "no-store" })
      .then((response) => response.ok ? response.json() : null)
      .then((payload: { data?: CmsCalculatorItem[] } | null) => {
        if (!cancelled && Array.isArray(payload?.data)) setCmsItems(payload.data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="rt-calculator">
      <div className="rt-calc-header">
        <h2>{ui.listTitle}</h2>
        <p>{ui.listSubtitle}</p>
      </div>

      <div className="rt-calc-grid">
        {list.map((c) => (
          <a
            key={c.id}
            href={c.href}
            className="rt-calc-card"
          >
            <h3>{c.title}</h3>
            <p>{c.desc}</p>
            <span className="rt-calc-card-arrow">{ui.cardCta}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

// 单页模式：只显示一个指定计算器（含返回列表链接和页面标题）
export function SingleCalculator({ id, lang = "zh" as CalcLang }: { id: CalculatorId; lang?: CalcLang }) {
  const meta = getCalculatorMeta(id, lang);
  const ui = UI_STRINGS[lang];
  const listPath = lang === "en" ? "/en/calculator" : "/calculator";

  return (
    <div className="rt-calculator rt-calculator-single">
      <div className="rt-calc-single-top">
        <a className="rt-calc-back" href={listPath}>{ui.backToList}</a>
      </div>
      <div className="rt-calc-header">
        <h2>{meta.title}</h2>
        <p>{meta.desc}</p>
      </div>
      <div className="rt-calc-panel">
        <CalculatorPanel id={id} lang={lang} />
      </div>
    </div>
  );
}

// ============================================================
// 1. 公英制单位换算器
// ============================================================
function UnitConverter({ lang = "zh" as CalcLang }: { lang?: CalcLang }) {
  const [mm, setMm] = useState("25.4");
  const [inch, setInch] = useState("1");
  const [dir, setDir] = useState<"mm2in" | "in2mm">("in2mm");

  const result = useMemo(() => {
    const mmVal = Number(mm);
    const inchVal = Number(inch);
    if (dir === "mm2in" && Number.isFinite(mmVal)) {
      return {
        in: mmVal / 25.4,
        ft: mmVal / 304.8,
        um: mmVal * 1000,
        cm: mmVal / 10,
        m: mmVal / 1000,
        fra: toFraction(mmVal / 25.4),
      };
    }
    if (dir === "in2mm" && Number.isFinite(inchVal)) {
      return {
        mm: inchVal * 25.4,
        um: inchVal * 25400,
        cm: inchVal * 2.54,
        m: inchVal * 0.0254,
        ft: inchVal / 12,
      };
    }
    return null;
  }, [mm, inch, dir]);

  function toFraction(decimal: number): string {
    if (!Number.isFinite(decimal)) return "";
    const whole = Math.floor(decimal);
    const frac = decimal - whole;
    const denominators = [2, 4, 8, 16, 32, 64];
    for (const d of denominators) {
      const n = Math.round(frac * d);
      if (Math.abs(n / d - frac) < 0.001) {
        if (n === 0) return whole ? `${whole}` : "0";
        return whole ? `${whole} ${n}/${d}` : `${n}/${d}`;
      }
    }
    return decimal.toFixed(4);
  }

  const T = SHARED_STRINGS[lang].unit;
  return (
    <div className="rt-calc-body">
      <div className="rt-calc-row">
        <label>{T.label}</label>
        <select value={dir} onChange={(e) => setDir(e.target.value as "mm2in" | "in2mm")}>
          <option value="in2mm">{T.in2mm}</option>
          <option value="mm2in">{T.mm2in}</option>
        </select>
      </div>
      {dir === "in2mm" ? (
        <div className="rt-calc-row">
          <label>{T.inputIn}</label>
          <input type="number" step="0.001" value={inch} onChange={(e) => setInch(e.target.value)} />
        </div>
      ) : (
        <div className="rt-calc-row">
          <label>{T.inputMm}</label>
          <input type="number" step="0.01" value={mm} onChange={(e) => setMm(e.target.value)} />
        </div>
      )}
      {result && (
        <dl className="rt-calc-result">
          {dir === "in2mm" ? (
            <>
              <div><dt>{T.mm}</dt><dd>{Number(result.mm).toFixed(4)}</dd></div>
              <div><dt>{T.cm}</dt><dd>{Number(result.cm).toFixed(4)}</dd></div>
              <div><dt>{T.m}</dt><dd>{Number(result.m).toFixed(5)}</dd></div>
              <div><dt>{T.um}</dt><dd>{Number(result.um).toFixed(1)}</dd></div>
              <div><dt>{T.ft}</dt><dd>{Number(result.ft).toFixed(5)}</dd></div>
            </>
          ) : (
            <>
              <div><dt>{T.in}</dt><dd>{Number(result.in).toFixed(6)}</dd></div>
              <div><dt>{T.fracIn}</dt><dd>{result.fra ?? ""} in</dd></div>
              <div><dt>{T.ft}</dt><dd>{Number(result.ft).toFixed(6)}</dd></div>
              <div><dt>{T.cm}</dt><dd>{Number(result.cm).toFixed(4)}</dd></div>
              <div><dt>{T.m}</dt><dd>{Number(result.m).toFixed(5)}</dd></div>
            </>
          )}
        </dl>
      )}
    </div>
  );
}

// ============================================================
// 2. 公制螺栓拧紧扭矩计算器
// ============================================================
const METRIC_THREAD_DATA: Record<string, { pitch: number; d2: number; d3: number; As: number }> = {
  M3: { pitch: 0.5, d2: 2.675, d3: 2.387, As: 5.03 },
  M4: { pitch: 0.7, d2: 3.545, d3: 3.141, As: 8.78 },
  M5: { pitch: 0.8, d2: 4.48, d3: 4.019, As: 14.2 },
  M6: { pitch: 1.0, d2: 5.35, d3: 4.773, As: 20.1 },
  M8: { pitch: 1.25, d2: 7.188, d3: 6.466, As: 39.7 },
  M10: { pitch: 1.5, d2: 9.026, d3: 8.16, As: 58 },
  M12: { pitch: 1.75, d2: 10.863, d3: 9.853, As: 84.3 },
  M14: { pitch: 2.0, d2: 12.701, d3: 11.546, As: 115 },
  M16: { pitch: 2.0, d2: 14.701, d3: 13.546, As: 157 },
  M18: { pitch: 2.5, d2: 16.376, d3: 14.933, As: 192 },
  M20: { pitch: 2.5, d2: 18.376, d3: 16.933, As: 245 },
  M22: { pitch: 2.5, d2: 20.376, d3: 18.933, As: 303 },
  M24: { pitch: 3.0, d2: 22.051, d3: 20.319, As: 353 },
  M27: { pitch: 3.0, d2: 25.051, d3: 23.319, As: 459 },
  M30: { pitch: 3.5, d2: 27.727, d3: 25.706, As: 561 },
  M33: { pitch: 3.5, d2: 30.727, d3: 28.706, As: 694 },
  M36: { pitch: 4.0, d2: 33.402, d3: 31.093, As: 817 },
  M39: { pitch: 4.0, d2: 36.402, d3: 34.093, As: 976 },
  M42: { pitch: 4.5, d2: 39.077, d3: 36.479, As: 1120 },
  M45: { pitch: 4.5, d2: 42.077, d3: 39.479, As: 1300 },
  M48: { pitch: 5.0, d2: 44.752, d3: 41.866, As: 1470 },
  M52: { pitch: 5.0, d2: 48.752, d3: 45.866, As: 1760 },
  M56: { pitch: 5.5, d2: 51.428, d3: 48.252, As: 2030 },
  M60: { pitch: 5.5, d2: 55.428, d3: 52.252, As: 2360 },
  M64: { pitch: 6.0, d2: 58.103, d3: 54.638, As: 2680 },
  M72: { pitch: 6.0, d2: 66.103, d3: 62.638, As: 3460 },
  M80: { pitch: 6.0, d2: 74.103, d3: 70.638, As: 4340 },
};

const GRADE_YIELD: Record<string, { Rp02: number; Rm: number }> = {
  "4.6": { Rp02: 240, Rm: 400 },
  "4.8": { Rp02: 320, Rm: 420 },
  "5.6": { Rp02: 300, Rm: 520 },
  "5.8": { Rp02: 400, Rm: 580 },
  "6.8": { Rp02: 480, Rm: 600 },
  "8.8": { Rp02: 640, Rm: 830 },
  "9.8": { Rp02: 720, Rm: 900 },
  "10.9": { Rp02: 940, Rm: 1040 },
  "12.9": { Rp02: 1100, Rm: 1220 },
  "SUS304/A2-70": { Rp02: 450, Rm: 700 },
  "SUS316/A4-70": { Rp02: 450, Rm: 700 },
  "SUS304/A2-80": { Rp02: 600, Rm: 800 },
};

function TorqueMetric({ lang = "zh" as CalcLang }: { lang?: CalcLang }) {
  const [size, setSize] = useState<keyof typeof METRIC_THREAD_DATA>("M8");
  const [grade, setGrade] = useState<keyof typeof GRADE_YIELD>("8.8");
  const [preloadRatio, setPreloadRatio] = useState(0.7);
  const [lubFactor, setLubFactor] = useState("dry");

  const kFactor = lubFactor === "dry" ? 0.2 : lubFactor === "oil" ? 0.15 : 0.12;
  const thread = METRIC_THREAD_DATA[size];
  const mat = GRADE_YIELD[grade];
  const nominalDia = Number(size.replace("M", ""));

  const { preload, torque, stress, safetyFactor } = useMemo(() => {
    const Ff = preloadRatio * mat.Rp02 * thread.As; // N
    const T = kFactor * nominalDia * 1e-3 * Ff; // N·m
    const sigma = Ff / thread.As; // MPa
    const sf = mat.Rp02 / sigma;
    return { preload: Ff, torque: T, stress: sigma, safetyFactor: sf };
  }, [preloadRatio, mat, thread, kFactor, nominalDia]);

  const C = SHARED_STRINGS[lang].common;
  const T = SHARED_STRINGS[lang].torqueMetric;
  return (
    <div className="rt-calc-body">
      <div className="rt-calc-row">
        <label>{C.size}</label>
        <select value={size} onChange={(e) => setSize(e.target.value as keyof typeof METRIC_THREAD_DATA)}>
          {Object.keys(METRIC_THREAD_DATA).map((k) => (
            <option key={k} value={k}>{k} (P={METRIC_THREAD_DATA[k].pitch})</option>
          ))}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{C.grade}</label>
        <select value={grade} onChange={(e) => setGrade(e.target.value as keyof typeof GRADE_YIELD)}>
          {Object.keys(GRADE_YIELD).map((k) => (
            <option key={k} value={k}>{k} (Rp0.2={GRADE_YIELD[k].Rp02}MPa)</option>
          ))}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{C.preloadRatio}</label>
        <input type="number" step="0.05" min="0.2" max="0.95" value={preloadRatio}
          onChange={(e) => setPreloadRatio(Math.min(0.95, Math.max(0.2, Number(e.target.value) || 0.7)))} />
        <span style={{ fontSize: 12, color: "#666" }}>{Math.round(preloadRatio * 100)}{C.percent}</span>
      </div>
      <div className="rt-calc-row">
        <label>{C.lubCondition}</label>
        <select value={lubFactor} onChange={(e) => setLubFactor(e.target.value)}>
          <option value="dry">{C.lubDry}</option>
          <option value="oil">{C.lubOil}</option>
          <option value="moly">{C.lubMoly}</option>
        </select>
      </div>
      <dl className="rt-calc-result">
        <div><dt>{T.resArea}</dt><dd>{thread.As} mm²</dd></div>
        <div><dt>{T.resForce}</dt><dd>{(preload / 1000).toFixed(2)} kN</dd></div>
        <div><dt>{T.resTorque}</dt><dd style={{ fontSize: "1.15rem", fontWeight: 700, color: "#2563eb" }}>{torque.toFixed(1)} N·m</dd></div>
        <div><dt>{T.resStress}</dt><dd>{stress.toFixed(1)} MPa</dd></div>
        <div><dt>{T.resSafety}</dt><dd>{safetyFactor.toFixed(2)}</dd></div>
      </dl>
      <p className="rt-calc-note">{T.note}</p>
    </div>
  );
}

// ============================================================
// 3. 螺纹底孔直径计算器（公制粗牙+细牙）
// ============================================================
const FINE_THREAD_DATA: Record<string, { pitch: number }> = {
  "M8×1": { pitch: 1 },
  "M10×1": { pitch: 1 },
  "M10×1.25": { pitch: 1.25 },
  "M12×1.25": { pitch: 1.25 },
  "M12×1.5": { pitch: 1.5 },
  "M14×1.25": { pitch: 1.25 },
  "M14×1.5": { pitch: 1.5 },
  "M16×1.5": { pitch: 1.5 },
  "M18×1.5": { pitch: 1.5 },
  "M18×2": { pitch: 2 },
  "M20×1.5": { pitch: 1.5 },
  "M20×2": { pitch: 2 },
  "M22×1.5": { pitch: 1.5 },
  "M22×2": { pitch: 2 },
  "M24×1.5": { pitch: 1.5 },
  "M24×2": { pitch: 2 },
  "M27×1.5": { pitch: 1.5 },
  "M27×2": { pitch: 2 },
  "M30×1.5": { pitch: 1.5 },
  "M30×2": { pitch: 2 },
  "M30×3": { pitch: 3 },
  "M36×2": { pitch: 2 },
  "M36×3": { pitch: 3 },
  "M42×2": { pitch: 2 },
  "M42×3": { pitch: 3 },
  "M48×2": { pitch: 2 },
  "M48×3": { pitch: 3 },
};

function TapDrill({ lang = "zh" as CalcLang }: { lang?: CalcLang }) {
  const TD = SHARED_STRINGS[lang].tapDrill;
  const C = SHARED_STRINGS[lang].common;

  const coarseOptions = Object.entries(METRIC_THREAD_DATA)
    .map(([k, v]) => ({ label: `${k} (P=${v.pitch}) ${TD.coarse}`, pitch: v.pitch, d: Number(k.replace("M", "")) }));
  const fineOptions = Object.entries(FINE_THREAD_DATA)
    .map(([k, v]) => ({ label: `${k} ${TD.fine}`, pitch: v.pitch, d: Number(k.split("×")[0].replace("M", "")) }));
  const allOptions = [...coarseOptions, ...fineOptions];

  const [index, setIndex] = useState(7);
  const opt = allOptions[index];
  const d0_75 = opt.d - 1.0825 * opt.pitch * 0.75;
  const d0_85 = opt.d - 1.0825 * opt.pitch * 0.85;
  const d0_iso = opt.d - 0.865 * opt.pitch;

  return (
    <div className="rt-calc-body">
      <div className="rt-calc-row">
        <label>{C.size}</label>
        <select value={index} onChange={(e) => setIndex(Number(e.target.value))}>
          {allOptions.map((o, i) => <option key={i} value={i}>{o.label}</option>)}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{TD.labelBase}</label>
        <select defaultValue="steel">
          <option value="steel">{TD.steel}</option>
          <option value="castiron">{TD.castiron}</option>
          <option value="aluminum">{TD.aluminum}</option>
          <option value="plastic">{TD.plastic}</option>
        </select>
      </div>
      <dl className="rt-calc-result">
        <div><dt>{TD.pitch}</dt><dd>{opt.pitch} mm</dd></div>
        <div><dt>{TD.iso}</dt><dd style={{ fontWeight: 700, color: "#2563eb" }}>Φ {d0_iso.toFixed(2)} mm</dd></div>
        <div><dt>{TD.h75}</dt><dd>Φ {d0_75.toFixed(2)} mm</dd></div>
        <div><dt>{TD.h85}</dt><dd>Φ {d0_85.toFixed(2)} mm</dd></div>
        <div><dt>{TD.chamfer}</dt><dd>{(opt.d + 0.5).toFixed(1)} × 90°</dd></div>
        <div><dt>{TD.blind}</dt><dd>{(1.5 * opt.d + opt.pitch).toFixed(1)} mm</dd></div>
      </dl>
      <p className="rt-calc-note">{TD.note}</p>
    </div>
  );
}

// ============================================================
// 4. 硬度换算（HRC / HB / HV 近似换算）
// ============================================================
function HardnessConverter({ lang = "zh" as CalcLang }: { lang?: CalcLang }) {
  const [source, setSource] = useState<"HRC" | "HB" | "HV">("HRC");
  const [value, setValue] = useState("30");
  const v = Number(value);
  const H = SHARED_STRINGS[lang].hardness;
  const C = SHARED_STRINGS[lang].common;

  const result = useMemo(() => {
    if (!Number.isFinite(v) || v <= 0) return null;
    let hrc: number | null = null;
    let hb: number | null = null;
    let hv: number | null = null;
    if (source === "HRC") {
      hrc = v;
      hb = v < 30 ? v * 11.6 + 120 : v * 10.8 + 160;
      hv = v < 30 ? v * 10.8 + 144 : v * 11.3 + 168;
    } else if (source === "HB") {
      hb = v;
      hrc = v > 388 ? (v - 160) / 10.8 : (v - 120) / 11.6;
      hv = 1.02 * v;
    } else {
      hv = v;
      hrc = v > 507 ? (v - 168) / 11.3 : (v - 144) / 10.8;
      hb = v / 1.02;
    }
    return { hrc, hb, hv, uts: hv ? 3.2 * hv : null };
  }, [source, v]);

  return (
    <div className="rt-calc-body">
      <div className="rt-calc-row">
        <label>{H.labelType}</label>
        <select value={source} onChange={(e) => setSource(e.target.value as "HRC" | "HB" | "HV")}>
          <option value="HRC">{H.hrc}</option>
          <option value="HB">{H.hb}</option>
          <option value="HV">{H.hv}</option>
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{H.labelValue}</label>
        <input type="number" step="0.1" value={value} onChange={(e) => setValue(e.target.value)} />
      </div>
      {result && result.hrc && result.hb && result.hv && result.uts !== null ? (
        <dl className="rt-calc-result">
          <div><dt>{H.hrcOut}</dt><dd>{result.hrc.toFixed(1)} HRC</dd></div>
          <div><dt>{H.hbOut}</dt><dd>{result.hb.toFixed(0)} HB</dd></div>
          <div><dt>{H.hvOut}</dt><dd>{result.hv.toFixed(0)} HV</dd></div>
          <div><dt>{H.uts}</dt><dd>{result.uts.toFixed(0)} MPa ≈ {(result.uts / 9.8067).toFixed(0)} kgf/mm²</dd></div>
        </dl>
      ) : (
        <p>{C.invalid}</p>
      )}
      <p className="rt-calc-note">{H.note}</p>
    </div>
  );
}

// ============================================================
// 5. 螺栓拉伸应力校核
// ============================================================
function TensionCheck({ lang = "zh" as CalcLang }: { lang?: CalcLang }) {
  const [size, setSize] = useState<keyof typeof METRIC_THREAD_DATA>("M12");
  const [grade, setGrade] = useState<keyof typeof GRADE_YIELD>("8.8");
  const [load, setLoad] = useState("20");
  const [dynamic, setDynamic] = useState(1.5);
  const thread = METRIC_THREAD_DATA[size];
  const mat = GRADE_YIELD[grade];
  const F = Number(load) * 1000;
  const totalLoad = F * dynamic;

  const sigma = totalLoad / thread.As;
  const sfYield = mat.Rp02 / sigma;
  const sfTensile = mat.Rm / sigma;

  const C = SHARED_STRINGS[lang].common;
  const T = SHARED_STRINGS[lang].tension;
  return (
    <div className="rt-calc-body">
      <div className="rt-calc-row">
        <label>{C.size}</label>
        <select value={size} onChange={(e) => setSize(e.target.value as keyof typeof METRIC_THREAD_DATA)}>
          {Object.keys(METRIC_THREAD_DATA).map((k) => (
            <option key={k} value={k}>{k} (As={METRIC_THREAD_DATA[k].As} mm²)</option>
          ))}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{C.grade}</label>
        <select value={grade} onChange={(e) => setGrade(e.target.value as keyof typeof GRADE_YIELD)}>
          {Object.keys(GRADE_YIELD).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{T.load}</label>
        <input type="number" step="1" value={load} onChange={(e) => setLoad(e.target.value)} />
        <span>{C.kN}</span>
      </div>
      <div className="rt-calc-row">
        <label>{T.dynamic}</label>
        <input type="number" step="0.1" min="1" value={dynamic}
          onChange={(e) => setDynamic(Math.max(1, Number(e.target.value) || 1))} />
        <span style={{ fontSize: 12, color: "#666" }}>×</span>
      </div>
      <dl className="rt-calc-result">
        <div><dt>{T.totalLoad}</dt><dd>{(totalLoad / 1000).toFixed(2)} kN</dd></div>
        <div><dt>{T.sigma}</dt><dd>{sigma.toFixed(1)} MPa</dd></div>
        <div><dt>{T.sfYield}（Rp0.2={mat.Rp02}MPa）</dt>
          <dd style={{ color: sfYield >= 1.5 ? "#059669" : "#dc2626", fontWeight: 700 }}>
            n = {sfYield.toFixed(2)} {sfYield >= 1.5 ? C.pass : C.fail15}
          </dd>
        </div>
        <div><dt>{T.sfTensile}（Rm={mat.Rm}MPa）</dt>
          <dd style={{ color: sfTensile >= 2.5 ? "#059669" : "#dc2626", fontWeight: 700 }}>
            n = {sfTensile.toFixed(2)} {sfTensile >= 2.5 ? C.pass : C.fail25}
          </dd>
        </div>
      </dl>
    </div>
  );
}

// ============================================================
// 6. 英制 UNC/UNF 扭矩计算器
// ============================================================
const UNIFIED_THREAD_DATA: Record<string, { tpi: number; d: number; As: number }> = {
  "1/4-20 UNC": { tpi: 20, d: 0.25, As: 0.0318 },
  "5/16-18 UNC": { tpi: 18, d: 0.3125, As: 0.0524 },
  "3/8-16 UNC": { tpi: 16, d: 0.375, As: 0.0775 },
  "7/16-14 UNC": { tpi: 14, d: 0.4375, As: 0.1063 },
  "1/2-13 UNC": { tpi: 13, d: 0.5, As: 0.1419 },
  "9/16-12 UNC": { tpi: 12, d: 0.5625, As: 0.182 },
  "5/8-11 UNC": { tpi: 11, d: 0.625, As: 0.226 },
  "3/4-10 UNC": { tpi: 10, d: 0.75, As: 0.334 },
  "7/8-9 UNC": { tpi: 9, d: 0.875, As: 0.462 },
  "1-8 UNC": { tpi: 8, d: 1.0, As: 0.606 },
  "1/4-28 UNF": { tpi: 28, d: 0.25, As: 0.0364 },
  "5/16-24 UNF": { tpi: 24, d: 0.3125, As: 0.058 },
  "3/8-24 UNF": { tpi: 24, d: 0.375, As: 0.0878 },
  "7/16-20 UNF": { tpi: 20, d: 0.4375, As: 0.1187 },
  "1/2-20 UNF": { tpi: 20, d: 0.5, As: 0.1599 },
  "9/16-18 UNF": { tpi: 18, d: 0.5625, As: 0.203 },
  "5/8-18 UNF": { tpi: 18, d: 0.625, As: 0.256 },
  "3/4-16 UNF": { tpi: 16, d: 0.75, As: 0.373 },
  "7/8-14 UNF": { tpi: 14, d: 0.875, As: 0.509 },
  "1-12 UNF": { tpi: 12, d: 1.0, As: 0.663 },
};

const SAE_GRADE: Record<string, { psi: number }> = {
  "SAE Grade 2": { psi: 74000 },
  "SAE Grade 5": { psi: 92000 },
  "SAE Grade 8": { psi: 130000 },
  "A307 Grade A": { psi: 36000 },
  "ASTM A325 (1型)": { psi: 92000 },
  "ASTM A490 (1型)": { psi: 130000 },
  "18-8 Stainless (A2-70)": { psi: 100000 },
};

function TorqueUnified({ lang = "zh" as CalcLang }: { lang?: CalcLang }) {
  const [size, setSize] = useState<keyof typeof UNIFIED_THREAD_DATA>("1/2-13 UNC");
  const [grade, setGrade] = useState<keyof typeof SAE_GRADE>("SAE Grade 5");
  const [preloadRatio, setPreloadRatio] = useState(0.7);
  const [lubFactor, setLubFactor] = useState("dry");
  const k = lubFactor === "dry" ? 0.2 : lubFactor === "oil" ? 0.15 : 0.12;
  const th = UNIFIED_THREAD_DATA[size];
  const g = SAE_GRADE[grade];

  const { preloadLbf, torqueFtLb, torqueInLb, torqueNm } = useMemo(() => {
    const F = preloadRatio * g.psi * th.As;
    const T_inlb = k * th.d * F;
    const T_ftlb = T_inlb / 12;
    const T_nm = T_ftlb * 1.35582;
    return { preloadLbf: F, torqueFtLb: T_ftlb, torqueInLb: T_inlb, torqueNm: T_nm };
  }, [preloadRatio, g, th, k]);

  const C = SHARED_STRINGS[lang].common;
  const U = SHARED_STRINGS[lang].torqueUnified;
  return (
    <div className="rt-calc-body">
      <div className="rt-calc-row">
        <label>{C.size}</label>
        <select value={size} onChange={(e) => setSize(e.target.value as keyof typeof UNIFIED_THREAD_DATA)}>
          {Object.keys(UNIFIED_THREAD_DATA).map((k) => (
            <option key={k} value={k}>{k} (As={UNIFIED_THREAD_DATA[k].As} in²)</option>
          ))}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{U.strengthClass}</label>
        <select value={grade} onChange={(e) => setGrade(e.target.value as keyof typeof SAE_GRADE)}>
          {Object.keys(SAE_GRADE).map((k) => (
            <option key={k} value={k}>{k} (Proof={SAE_GRADE[k].psi.toLocaleString()} psi)</option>
          ))}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{C.preloadRatioShort}</label>
        <input type="number" step="0.05" min="0.2" max="0.95" value={preloadRatio}
          onChange={(e) => setPreloadRatio(Math.min(0.95, Math.max(0.2, Number(e.target.value) || 0.7)))} />
        <span style={{ fontSize: 12, color: "#666" }}>{Math.round(preloadRatio * 100)}{C.percent}</span>
      </div>
      <div className="rt-calc-row">
        <label>{C.lubConditionShort}</label>
        <select value={lubFactor} onChange={(e) => setLubFactor(e.target.value)}>
          <option value="dry">{C.lubDry}</option>
          <option value="oil">{C.lubOil2}</option>
          <option value="moly">{C.lubMoly}</option>
        </select>
      </div>
      <dl className="rt-calc-result">
        <div><dt>{U.resPreload}</dt><dd>{preloadLbf.toFixed(0)} lbf = {(preloadLbf * 4.44822).toFixed(1)} N</dd></div>
        <div><dt>{U.resImperial}</dt>
          <dd style={{ fontWeight: 700, color: "#2563eb", fontSize: "1.15rem" }}>
            {torqueFtLb.toFixed(1)} ft·lb = {torqueInLb.toFixed(0)} in·lb
          </dd>
        </div>
        <div><dt>{U.resMetric}</dt><dd>{torqueNm.toFixed(1)} N·m</dd></div>
      </dl>
    </div>
  );
}

// ============================================================
// 7. 螺栓剪切强度校核（单剪切面）
// ============================================================
function ShearCheck({ lang = "zh" as CalcLang }: { lang?: CalcLang }) {
  const [size, setSize] = useState<keyof typeof METRIC_THREAD_DATA>("M10");
  const [grade, setGrade] = useState<keyof typeof GRADE_YIELD>("10.9");
  const [load, setLoad] = useState("12");
  const [planes, setPlanes] = useState<1 | 2>(1);
  const thread = METRIC_THREAD_DATA[size];
  const mat = GRADE_YIELD[grade];
  const nominalDia = Number(size.replace("M", ""));
  const F = Number(load) * 1000;
  const AShear = planes * Math.PI * Math.pow(nominalDia, 2) / 4;
  const tau = F / AShear;
  const tauY = 0.58 * mat.Rp02;
  const tauU = 0.6 * mat.Rm;
  const sfY = tauY / tau;
  const sfU = tauU / tau;

  const C = SHARED_STRINGS[lang].common;
  const S = SHARED_STRINGS[lang].shear;
  return (
    <div className="rt-calc-body">
      <div className="rt-calc-row">
        <label>{C.shortSize}</label>
        <select value={size} onChange={(e) => setSize(e.target.value as keyof typeof METRIC_THREAD_DATA)}>
          {Object.keys(METRIC_THREAD_DATA).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{C.shortGrade}</label>
        <select value={grade} onChange={(e) => setGrade(e.target.value as keyof typeof GRADE_YIELD)}>
          {Object.keys(GRADE_YIELD).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{S.planes}</label>
        <select value={planes} onChange={(e) => setPlanes(Number(e.target.value) as 1 | 2)}>
          <option value={1}>{S.plane1}</option>
          <option value={2}>{S.plane2}</option>
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{S.load}</label>
        <input type="number" step="1" value={load} onChange={(e) => setLoad(e.target.value)} />
        <span>{C.kN}</span>
      </div>
      <dl className="rt-calc-result">
        <div><dt>{S.totalArea}（{planes}{S.planesSuffix}）</dt><dd>{AShear.toFixed(1)} mm²</dd></div>
        <div><dt>{S.tau}</dt><dd>{tau.toFixed(1)} MPa</dd></div>
        <div><dt>{S.sfY}（0.58·Rp0.2={tauY.toFixed(0)}MPa）</dt>
          <dd style={{ color: sfY >= 1.5 ? "#059669" : "#dc2626", fontWeight: 700 }}>
            n_s = {sfY.toFixed(2)} {sfY >= 1.5 ? C.pass : C.fail15}
          </dd>
        </div>
        <div><dt>{S.sfU}（0.6·Rm={tauU.toFixed(0)}MPa）</dt>
          <dd style={{ color: sfU >= 2.5 ? "#059669" : "#dc2626", fontWeight: 700 }}>
            n_u = {sfU.toFixed(2)} {sfU >= 2.5 ? C.pass : C.fail25}
          </dd>
        </div>
      </dl>
      <p className="rt-calc-note">{S.note}</p>
    </div>
  );
}

// ============================================================
// 8. 螺纹最小旋合长度（防螺纹滑牙）
// ============================================================
function MinEngagement({ lang = "zh" as CalcLang }: { lang?: CalcLang }) {
  const [size, setSize] = useState<keyof typeof METRIC_THREAD_DATA>("M12");
  const [grade, setGrade] = useState<keyof typeof GRADE_YIELD>("8.8");
  const [base, setBase] = useState<"steel" | "aluminum" | "castiron">("aluminum");
  const thread = METRIC_THREAD_DATA[size];
  const boltRp = GRADE_YIELD[grade].Rp02;
  const baseRp =
    base === "steel" ? 240 :
    base === "castiron" ? 160 :
    110;

  const nominalDia = Number(size.replace("M", ""));
  const circlesPerSide = (boltRp / baseRp) * (thread.As / (Math.PI * nominalDia * thread.d3 * 0.5));
  const minTurns = Math.ceil(circlesPerSide * 1.25);
  const minL = minTurns * thread.pitch;

  const C = SHARED_STRINGS[lang].common;
  const M = SHARED_STRINGS[lang].engagement;
  return (
    <div className="rt-calc-body">
      <div className="rt-calc-row">
        <label>{C.size}</label>
        <select value={size} onChange={(e) => setSize(e.target.value as keyof typeof METRIC_THREAD_DATA)}>
          {Object.keys(METRIC_THREAD_DATA).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{M.boltGrade}</label>
        <select value={grade} onChange={(e) => setGrade(e.target.value as keyof typeof GRADE_YIELD)}>
          {Object.keys(GRADE_YIELD).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{M.baseMat}</label>
        <select value={base} onChange={(e) => setBase(e.target.value as typeof base)}>
          <option value="steel">{M.steel}</option>
          <option value="castiron">{M.castiron}</option>
          <option value="aluminum">{M.aluminum}</option>
        </select>
      </div>
      <dl className="rt-calc-result">
        <div><dt>{M.boltRp}</dt><dd>{boltRp} MPa</dd></div>
        <div><dt>{M.baseRp}</dt><dd>{baseRp} MPa</dd></div>
        <div><dt>{M.ratio}</dt><dd>{(boltRp / baseRp).toFixed(2)} : 1</dd></div>
        <div><dt>{M.minTurns}</dt><dd style={{ fontWeight: 700, color: "#2563eb" }}>{minTurns} {M.turns}</dd></div>
        <div><dt>{M.minLen}</dt><dd style={{ fontWeight: 700, color: "#2563eb", fontSize: "1.15rem" }}>≥ {minL.toFixed(1)} {M.mm}</dd></div>
        <div><dt>{M.blind}</dt><dd>{(minL + 2 * thread.pitch).toFixed(1)} {M.mm} {M.mmSuffix}</dd></div>
      </dl>
      <p className="rt-calc-note">{M.note}</p>
    </div>
  );
}

// ============================================================
// 9. 螺栓长度选型（通孔+螺母场景）
// ============================================================
const STANDARD_LENGTHS_METRIC = [
  6, 8, 10, 12, 14, 16, 18, 20, 22, 25, 28, 30, 32, 35, 38, 40, 45, 50, 55, 60, 65, 70, 75,
  80, 85, 90, 95, 100, 110, 120, 130, 140, 150, 160, 170, 180, 190, 200, 220, 240, 260, 280, 300,
];

const NUT_HEIGHT_METRIC: Record<string, number> = {
  M3: 2.4, M4: 3.2, M5: 4, M6: 5, M8: 6.5, M10: 8, M12: 10, M14: 11, M16: 13, M18: 14,
  M20: 16, M22: 18, M24: 19, M27: 22, M30: 24, M33: 26, M36: 29, M39: 31, M42: 34, M45: 36,
  M48: 39, M52: 42, M56: 45, M60: 48, M64: 51, M72: 58, M80: 64,
};

const WASHER_THICKNESS: Record<string, number> = {
  M3: 0.5, M4: 0.8, M5: 1, M6: 1.6, M8: 1.6, M10: 2, M12: 2.5, M14: 2.5, M16: 3,
  M18: 3, M20: 3, M22: 3, M24: 4, M27: 4, M30: 4, M33: 5, M36: 5, M39: 6, M42: 6,
  M45: 6, M48: 8, M52: 8, M56: 8, M60: 8, M64: 8, M72: 10, M80: 10,
};

function BoltLengthSelector({ lang = "zh" as CalcLang }: { lang?: CalcLang }) {
  const [size, setSize] = useState<keyof typeof METRIC_THREAD_DATA>("M12");
  const [stackup, setStackup] = useState("20");
  const [washerCount, setWasherCount] = useState<0 | 1 | 2>(2);
  const [lockNut, setLockNut] = useState(false);
  const [chamferAllow, setChamferAllow] = useState(true);

  const nutH = NUT_HEIGHT_METRIC[size] ?? 10;
  const wash = WASHER_THICKNESS[size] ?? 2;
  const pitch = METRIC_THREAD_DATA[size].pitch;
  const L_needed =
    Number(stackup) + washerCount * wash + (lockNut ? nutH * 1.5 : nutH) + (chamferAllow ? pitch * 2 : pitch * 3);
  const L_standard = STANDARD_LENGTHS_METRIC.find((l) => l >= L_needed) ?? STANDARD_LENGTHS_METRIC.at(-1)!;
  const exposure = L_standard - (Number(stackup) + washerCount * wash + nutH);

  const C = SHARED_STRINGS[lang].common;
  const B = SHARED_STRINGS[lang].boltLen;
  return (
    <div className="rt-calc-body">
      <div className="rt-calc-row">
        <label>{C.boltSize}</label>
        <select value={size} onChange={(e) => setSize(e.target.value as keyof typeof METRIC_THREAD_DATA)}>
          {Object.keys(METRIC_THREAD_DATA).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{B.stackup}</label>
        <input type="number" step="1" value={stackup} onChange={(e) => setStackup(e.target.value)} />
        <span>mm</span>
      </div>
      <div className="rt-calc-row">
        <label>{B.washers}</label>
        <select value={washerCount} onChange={(e) => setWasherCount(Number(e.target.value) as 0 | 1 | 2)}>
          <option value={0}>{B.washer0}</option>
          <option value={1}>{B.washer1}</option>
          <option value={2}>{B.washer2}</option>
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{B.nutType}</label>
        <select value={lockNut ? 1 : 0} onChange={(e) => setLockNut(Number(e.target.value) === 1)}>
          <option value={0}>{B.nutStd}</option>
          <option value={1}>{B.nutLock}</option>
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{B.chamfer}</label>
        <select value={chamferAllow ? 1 : 0} onChange={(e) => setChamferAllow(Number(e.target.value) === 1)}>
          <option value={1}>{B.chamferNorm}</option>
          <option value={0}>{B.chamferCon}</option>
        </select>
      </div>
      <dl className="rt-calc-result">
        <div><dt>{B.lmin}</dt><dd>{L_needed.toFixed(1)} mm</dd></div>
        <div><dt>{B.std}</dt>
          <dd style={{ fontWeight: 700, color: "#2563eb", fontSize: "1.15rem" }}>{size} × {L_standard}</dd>
        </div>
        <div><dt>{B.exposure}</dt>
          <dd>{exposure.toFixed(1)} mm ≈ {(exposure / pitch).toFixed(1)} {B.teeth}
            <span style={{ color: exposure >= pitch ? " #059669" : " #dc2626" }}>
              （{exposure >= pitch ? B.exposureOk : B.exposureBad}）
            </span>
          </dd>
        </div>
        <div><dt>{B.nutH}</dt><dd>{nutH} mm</dd></div>
        <div><dt>{B.wash}</dt><dd>{wash} mm</dd></div>
      </dl>
    </div>
  );
}

// ============================================================
// 10. 连接面滑移安全系数（预紧摩擦型连接）
// ============================================================
function SlipFactorCheck({ lang = "zh" as CalcLang }: { lang?: CalcLang }) {
  const [size, setSize] = useState<keyof typeof METRIC_THREAD_DATA>("M16");
  const [grade, setGrade] = useState<keyof typeof GRADE_YIELD>("10.9");
  const [faying, setFaying] = useState<"sandblast" | "millscale" | "painted" | "galvanized">("sandblast");
  const [boltsCount, setBoltsCount] = useState("4");
  const [preloadRatio, setPreloadRatio] = useState(0.7);
  const [shearLoad, setShearLoad] = useState("40");
  const thread = METRIC_THREAD_DATA[size];
  const mat = GRADE_YIELD[grade];

  const mu =
    faying === "sandblast" ? 0.5 :
    faying === "millscale" ? 0.45 :
    faying === "painted" ? 0.3 :
    0.35;

  const Ff_one = preloadRatio * mat.Rp02 * thread.As;
  const n = Number(boltsCount) || 1;
  const TotalFf = Ff_one * n;
  const Q_friction = mu * TotalFf;
  const F_trans = Number(shearLoad) * 1000;
  const sf_slip = Q_friction / F_trans;

  const C = SHARED_STRINGS[lang].common;
  const S = SHARED_STRINGS[lang].slip;
  return (
    <div className="rt-calc-body">
      <div className="rt-calc-row">
        <label>{C.boltSize}</label>
        <select value={size} onChange={(e) => setSize(e.target.value as keyof typeof METRIC_THREAD_DATA)}>
          {Object.keys(METRIC_THREAD_DATA).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{C.shortGrade}</label>
        <select value={grade} onChange={(e) => setGrade(e.target.value as keyof typeof GRADE_YIELD)}>
          {Object.keys(GRADE_YIELD).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{S.faying}</label>
        <select value={faying} onChange={(e) => setFaying(e.target.value as typeof faying)}>
          <option value="sandblast">{S.sb}</option>
          <option value="millscale">{S.ms}</option>
          <option value="galvanized">{S.gd}</option>
          <option value="painted">{S.pt}</option>
        </select>
      </div>
      <div className="rt-calc-row">
        <label>{S.count}</label>
        <input type="number" step="1" min="1" value={boltsCount} onChange={(e) => setBoltsCount(e.target.value)} />
      </div>
      <div className="rt-calc-row">
        <label>{C.preloadRatioShort}</label>
        <input type="number" step="0.05" min="0.2" max="0.9" value={preloadRatio}
          onChange={(e) => setPreloadRatio(Math.min(0.9, Math.max(0.2, Number(e.target.value) || 0.7)))} />
        <span style={{ fontSize: 12, color: "#666" }}>{Math.round(preloadRatio * 100)}{C.percent}</span>
      </div>
      <div className="rt-calc-row">
        <label>{S.total}</label>
        <input type="number" step="1" value={shearLoad} onChange={(e) => setShearLoad(e.target.value)} />
        <span>{C.kN}</span>
      </div>
      <dl className="rt-calc-result">
        <div><dt>{S.onePreload}</dt><dd>{(Ff_one / 1000).toFixed(2)} kN</dd></div>
        <div><dt>{S.normal}（{n} {S.countSuffix}）</dt><dd>{(TotalFf / 1000).toFixed(1)} kN</dd></div>
        <div><dt>{S.friction}</dt><dd>{(Q_friction / 1000).toFixed(1)} kN</dd></div>
        <div><dt>{S.kf}</dt>
          <dd style={{ color: sf_slip >= 1.3 ? "#059669" : "#dc2626", fontWeight: 700, fontSize: "1.15rem" }}>
            Kf = {sf_slip.toFixed(2)} {sf_slip >= 1.3 ? C.pass13 : C.fail13}
          </dd>
        </div>
        <div><dt>{S.mu}</dt><dd>{mu}</dd></div>
      </dl>
      <p className="rt-calc-note">{S.note}</p>
    </div>
  );
}
